"use client";

import { WeatherIcon } from "@/components/WeatherIcon";
import { weatherKindOf } from "@/greenpak/weather-conditions";

import { useEffect, useState } from "react";
import { locateByIp, weatherForMany } from "@/greenpak/geo";
import type { CurrentWeather } from "@/greenpak/geo";
import { HelplineBarButton } from "./HelplineButton";
import { useIsInternationalVisitor } from "@/greenpak/use-visitor-country";

type State =
  | { status: "loading" }
  | { status: "denied" }
  | { status: "ready"; wx: CurrentWeather; city: string };

export function WeatherBar() {
  // The live lookup picks the helpline, not a server geoip guess: the offline
  // database mis-resolved a real Pakistani visitor's ISP to another country,
  // which is how someone in Rawalpindi saw the international number. It is
  // the same lookup that gets this bar's own city right.
  const liveIsInternational = useIsInternationalVisitor();
  const [state, setState] = useState<State>({ status: "loading" });
  const [precise, setPrecise] = useState(false);

  // On load, derive a rough location from IP (no permission prompt) so the bar is
  // useful immediately. Precise device location is opt-in via the button below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = await locateByIp();
      if (cancelled) return;
      if (!loc) {
        setState({ status: "denied" });
        return;
      }
      const [wx] = await weatherForMany([{ lat: loc.lat, lng: loc.lon }]);
      if (cancelled) return;
      setState(wx ? { status: "ready", wx, city: loc.place || "Your area" } : { status: "denied" });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Only ask for the browser geolocation permission when the user explicitly opts in.
  function usePreciseLocation() {
    if (!navigator.geolocation) return;
    setPrecise(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const [wx] = await weatherForMany([{ lat, lng }]);
        if (!wx) return;
        let city = "Your location";
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`,
            { headers: { "Accept-Language": "en" } },
          );
          const data = await res.json();
          city =
            data?.address?.city ||
            data?.address?.town ||
            data?.address?.village ||
            data?.address?.county ||
            city;
        } catch {
          // keep fallback city
        }
        setState({ status: "ready", wx, city });
      },
      () => setPrecise(false),
      { timeout: 8000 },
    );
  }

  return (
    <div className="flex min-h-7 items-center gap-2 overflow-hidden border-b border-[var(--border-soft)] bg-[var(--panel)] px-4 py-1 text-[11px] tracking-wide text-[var(--muted)] sm:gap-3 sm:px-5 sm:py-1.5 sm:text-sm">
      <span className="shrink-0 text-[9px] font-bold tracking-[0.14em] uppercase text-[var(--accent)] sm:text-[11px] sm:tracking-[0.16em]">
        Live
      </span>

      {state.status === "loading" && <span className="opacity-40">Checking local weather…</span>}

      {state.status === "denied" && (
        <span className="opacity-50">Weather unavailable right now</span>
      )}

      {state.status === "ready" && (
        <span className="flex items-center gap-2">
          <span className="inline-flex items-center text-[var(--accent)]">
            <WeatherIcon kind={weatherKindOf(state.wx)} size={14} />
          </span>
          <b className="font-semibold text-[var(--foreground)]">{state.city}</b>
          <span className="text-[var(--accent)] font-medium">{state.wx.temp}°C</span>
          <span className="opacity-75">{state.wx.label}</span>
          {typeof state.wx.windKph === "number" && (
            <span className="hidden sm:inline opacity-60">· {state.wx.windKph} km/h</span>
          )}
          {!precise && (
            <button
              type="button"
              onClick={usePreciseLocation}
              className="ml-1 hidden items-center gap-1 rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)] cursor-pointer transition-colors duration-150 hover:border-[color-mix(in_srgb,var(--accent)_50%,transparent)] hover:text-[var(--foreground)] sm:inline-flex"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11Z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
              Use my location
            </button>
          )}
        </span>
      )}

      {/* The Carl Sagan quote used to sit here, centred, at xl+ only. It now
          opens the homepage hero above the PALE BLUE DOT wordmark instead
          (HeroBrand.tsx), where it gets read rather than skimmed past — and
          where every breakpoint sees it, not just the widest ones. */}

      <span className="ml-auto flex shrink-0 items-center gap-3">
        <HelplineBarButton isInternational={liveIsInternational} />
        <span className="hidden whitespace-nowrap lg:inline text-[11px]">
          Request-based planning · human-led
        </span>
      </span>
    </div>
  );
}
